import { CircularProgress, css, styled } from "@mui/material";
import type { HierarcicalObjectReference } from "@novorender/data-js-api";
import { ChevronRight, Eye, EyeOff } from "@procore/core-icons";
import { Box, Checkbox, colors, Dropdown, Typography } from "@procore/core-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import type { Node, Tree } from "components/objectTree/types";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import type { ModelTreeNodeData } from "features/modelTree/types";
import {
    renderActions,
    selectDefaultVisibility,
    selectMainObject,
    selectMainObjects,
    selectPreIsolationVisibility,
} from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { useAbortController } from "hooks/useAbortController";
import {
    bulkHideAllFromFilteredTree,
    bulkHideAllHierarchies,
    bulkSelectAllFromFilteredTree,
    bulkSelectAllHierarchies,
    bulkShowAllFromFilteredTree,
    bulkShowAllHierarchies,
    bulkUnselectAllFromFilteredTree,
    bulkUnselectAllHierarchies,
    unselectAll,
} from "utils/objectActions";
import { startMeasure } from "utils/performanceMeasure";
import type { SelectionSet } from "utils/selectionSet";
import type { GroupState } from "utils/treeHelpers";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

import { CHEVRON_SIZE } from "../constants";
import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";
import { createBulkActions } from "../utils/bulkActions";
import { VisibilityButton } from "./VisibilityButton";

interface ObjectTreeControlsBarProps {
    tree: Tree<ModelTreeNodeData>;
    filteredObjectIds: Set<number> | null;
    hasNoResults: boolean;
    onCollapseAll: () => void;
    /** Optional border to apply at the bottom of the controls bar. */
    borderBottom?: string;
    /**
     * When true, skip the outer Box wrapper (padding, border, background, transition)
     * and render the controls as a fragment. Use when the caller already provides
     * the row chrome (e.g. `Window`'s `bulkActions` slot).
     */
    unwrapped?: boolean;
}

const ChevronButton = styled(Box, {
    shouldForwardProp: (prop) => prop !== "collapsed",
})<{ disabled?: boolean; collapsed?: boolean }>(
    ({ disabled, collapsed }) => css`
        height: ${CHEVRON_SIZE}px;
        cursor: ${disabled ? "not-allowed" : "pointer"};
        transform: rotate(${collapsed ? "0deg" : "90deg"});
        transition: transform 0.2s ease;
        border-radius: 4px;
        display: flex;
        align-items: center;
        opacity: ${disabled ? 0.4 : 1};

        &:hover {
            background-color: ${disabled ? "transparent" : colors.gray90};
        }
    `,
);

/**
 * Generic helper to calculate the group state (all/some/none) of a set of IDs
 * relative to the tree structure.
 * Used for both selection and visibility states.
 */
function getRootGroupState(
    rootNode: Node<ModelTreeNodeData>,
    tree: Tree<ModelTreeNodeData>,
    ids: SelectionSet,
    filteredObjectIds: Set<number> | null = null,
): GroupState {
    if (ids.size === 0) {
        return "none";
    }

    if (rootNode.children && filteredObjectIds === null) {
        let allTopLevelItemsInSet = true;
        for (const childPath of rootNode.children) {
            const child = tree[childPath];
            if (child && !ids.has(child.data.object.id)) {
                allTopLevelItemsInSet = false;
                break;
            }
        }

        if (allTopLevelItemsInSet) {
            return "all";
        }
    }

    if (filteredObjectIds !== null) {
        const totalFiltered = filteredObjectIds.size;
        if (totalFiltered === 0) {
            return "none";
        }

        let idsInSetCount = 0;
        for (const id of filteredObjectIds) {
            if (ids.has(id)) {
                idsInSetCount++;
            }
        }

        if (idsInSetCount === totalFiltered) {
            return "all";
        }
    }

    return "some";
}

function getTopLevelObjectsFromTree(
    rootNode: Node<ModelTreeNodeData>,
    tree: Tree<ModelTreeNodeData>,
): HierarcicalObjectReference[] {
    if (!rootNode.children || rootNode.children.length === 0) {
        return rootNode.data?.object ? [rootNode.data.object] : [];
    }

    return rootNode.children
        .map((childPath) => tree[childPath])
        .filter((node) => node?.data?.object)
        .map((node) => node.data.object);
}

/**
 * Shared controls bar for the object tree showing the global selection checkbox,
 * collapse-all chevron, bulk Actions dropdown, selection count, and visibility toggle.
 *
 * Used by the ObjectTreeWindow header (desktop floating window and mobile panel/drawer via SlottedWindow)
 * so that the bulk Actions dropdown surfaces consistently in every UI surface.
 */
export function ObjectTreeControlsBar({
    tree,
    filteredObjectIds,
    hasNoResults,
    onCollapseAll,
    borderBottom,
    unwrapped = false,
}: ObjectTreeControlsBarProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const mainObject = useAppSelector(selectMainObject);
    const mainObjects = useAppSelector(selectMainObjects);
    const highlightedSet = useHighlighted();
    const { idArr: geometryIds, ids: highlightedIdsRecord } = highlightedSet;
    const hiddenSet = useHidden();
    const { ids: hiddenIdsRecord, lastHiddenIds } = hiddenSet;
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;
    const {
        state: { dataAccess },
    } = useExplorerGlobals(true);
    const [isLoading, setIsLoading] = useState(false);
    const [abortController, abortSelection] = useAbortController();

    // V1 tree selection never populates mainObjects; fall back to the full geometry
    // set so count, disabled checks, and labels all reflect the actual selection.
    const effectiveMainObjects = mainObjects.length > 0 ? mainObjects : geometryIds;
    const selectedCount = effectiveMainObjects.length;
    const hasSelection = selectedCount > 0;
    const isNoResultsWithoutSelection = hasNoResults && !hasSelection;
    const isCheckboxDisabled = isLoading || isNoResultsWithoutSelection;
    const isVisibilityDisabled = isLoading || isNoResultsWithoutSelection;

    const rootSelectionState = useMemo(() => {
        const rootNode = tree[""];
        if (!rootNode) {
            return "none" as const;
        }

        return getRootGroupState(rootNode, tree, highlightedIdsRecord, filteredObjectIds);
    }, [tree, filteredObjectIds, highlightedIdsRecord]);

    const rootVisibilityState = useMemo(() => {
        const rootNode = tree[""];
        if (!rootNode) {
            return "none" as const;
        }

        const state = getRootGroupState(rootNode, tree, isolationVisibleIds ?? hiddenIdsRecord, filteredObjectIds);
        return isolationVisibleIds ? invertGroupState(state) : state;
    }, [tree, filteredObjectIds, hiddenIdsRecord, isolationVisibleIds]);

    const selectedVisibilityState = useMemo((): GroupState => {
        if (!hasSelection) {
            return "none";
        }

        let allHidden = true;
        let anyHidden = false;

        for (const id of effectiveMainObjects) {
            const isHidden = isolationVisibleIds ? !isolationVisibleIds.has(id) : hiddenIdsRecord.has(id);
            if (isHidden) {
                anyHidden = true;
            } else {
                allHidden = false;
            }
            if (anyHidden && !allHidden) {
                break;
            }
        }

        if (allHidden) {
            return "all";
        }
        if (anyHidden) {
            return "some";
        }
        return "none";
    }, [hasSelection, effectiveMainObjects, hiddenIdsRecord, isolationVisibleIds]);

    const effectiveVisibilityState = hasSelection ? selectedVisibilityState : rootVisibilityState;

    let isFullyCollapsed = true;
    const rootNode = tree[""];
    if (rootNode?.children) {
        for (const childPath of rootNode.children) {
            const child = tree[childPath];
            if (child?.expanded) {
                isFullyCollapsed = false;
                break;
            }
        }
    }

    const handleGlobalCheckboxChange = async () => {
        if (isLoading) {
            return;
        }

        if (hasNoResults) {
            if (!hasSelection) {
                return;
            }

            unselectAll({
                dispatch,
                dispatchHighlighted,
                currentHighlightedIds: geometryIds,
                currentMainObject: mainObject,
                currentMainObjects: mainObjects,
            });
            return;
        }

        if (!dataAccess) {
            return;
        }

        const rootNode = tree[""];
        if (!rootNode) {
            return;
        }

        const objects = getTopLevelObjectsFromTree(rootNode, tree);
        if (objects.length === 0) {
            return;
        }

        if (rootSelectionState !== "all") {
            void track(Events.objectTree.selectAll);
        }

        abortSelection();

        const isUnselect = rootSelectionState === "all";
        const perf = startMeasure(isUnselect ? "selectAll.unselect" : "selectAll.select");

        try {
            let affectedIds: number[];
            setIsLoading(true);
            try {
                if (filteredObjectIds != null) {
                    // Filtered tree: every matched id is already known synchronously, single dispatch
                    affectedIds = isUnselect
                        ? bulkUnselectAllFromFilteredTree({
                              filteredObjectIds,
                              dispatchHighlighted,
                              parentMeasure: perf,
                          })
                        : bulkSelectAllFromFilteredTree({
                              filteredObjectIds,
                              dispatchHighlighted,
                              parentMeasure: perf,
                          });
                } else {
                    // Normal tree: parallel network fetches, single dispatch
                    affectedIds = isUnselect
                        ? await bulkUnselectAllHierarchies({
                              nodes: objects,
                              dataAccess,
                              abortSignal: abortController.current.signal,
                              dispatchHighlighted,
                              parentMeasure: perf,
                          })
                        : await bulkSelectAllHierarchies({
                              nodes: objects,
                              dataAccess,
                              abortSignal: abortController.current.signal,
                              dispatchHighlighted,
                              parentMeasure: perf,
                          });
                }
            } finally {
                setIsLoading(false);
            }

            perf.mark("dispatchComplete", { affectedIds: affectedIds.length });

            if (affectedIds.length > 0) {
                const clearedMainObjectId =
                    isUnselect && mainObject !== undefined && affectedIds.includes(mainObject) ? mainObject : undefined;
                const clearedMainObjects = isUnselect ? mainObjects : [];
                const shrunkAffected = snapshotUndoIds(affectedIds);

                if (clearedMainObjectId !== undefined) {
                    dispatch(renderActions.setMainObject(undefined));
                }
                if (isUnselect) {
                    dispatch(renderActions.clearMainObjects());
                }

                dispatch(
                    undoRedoActions.push({
                        description: isUnselect ? "Unselect objects" : "Select objects",
                        undoCallback: () => {
                            const ids = restoreUndoIds(shrunkAffected);
                            dispatchHighlighted(isUnselect ? highlightActions.add(ids) : highlightActions.remove(ids));
                            if (clearedMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(clearedMainObjectId));
                            }
                            if (isUnselect) {
                                dispatch(renderActions.setMainObjects(clearedMainObjects));
                            }
                        },
                        redoCallback: () => {
                            const ids = restoreUndoIds(shrunkAffected);
                            dispatchHighlighted(isUnselect ? highlightActions.remove(ids) : highlightActions.add(ids));
                            if (clearedMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(undefined));
                            }
                            if (isUnselect) {
                                dispatch(renderActions.clearMainObjects());
                            }
                        },
                    }),
                );
            }

            perf.end({ totalAffected: affectedIds.length, isUnselect });
        } catch (error) {
            perf.end({ error: true, isUnselect });
            if (!abortController.current.signal.aborted) {
                console.error("Global selection failed:", error);
            }
        }
    };

    const handleGlobalVisibilityToggle = async () => {
        if (isLoading || !dataAccess) {
            return;
        }

        if (isNoResultsWithoutSelection) {
            return;
        }

        if (hasSelection) {
            const isUnhide = effectiveVisibilityState === "all";
            const ids = effectiveMainObjects;

            if (ids.length === 0) {
                return;
            }

            if (isolationVisibleIds) {
                isolation.setIdsVisible(ids, isUnhide);
                return;
            }

            if (effectiveVisibilityState !== "all") {
                void track(Events.objectTree.hideAll);
            }

            if (isUnhide) {
                dispatchHidden(hiddenActions.remove(ids));
            } else {
                dispatchHidden(hiddenActions.add(ids));
                dispatchHidden(hiddenActions.setLastHiddenIds(ids));
            }

            const shrunkIds = snapshotUndoIds(ids);

            dispatch(
                undoRedoActions.push({
                    description: isUnhide ? "Show objects" : "Hide objects",
                    undoCallback: () => {
                        const restored = restoreUndoIds(shrunkIds);
                        dispatchHidden(isUnhide ? hiddenActions.add(restored) : hiddenActions.remove(restored));
                    },
                    redoCallback: () => {
                        const restored = restoreUndoIds(shrunkIds);
                        dispatchHidden(isUnhide ? hiddenActions.remove(restored) : hiddenActions.add(restored));
                    },
                }),
            );
            return;
        }

        const rootNode = tree[""];
        if (!rootNode) {
            return;
        }

        const objects = getTopLevelObjectsFromTree(rootNode, tree);
        if (objects.length === 0) {
            return;
        }

        if (effectiveVisibilityState !== "all") {
            void track(Events.objectTree.hideAll);
        }

        abortSelection();

        const isShow = effectiveVisibilityState === "all";
        const perf = startMeasure(isShow ? "hideAll.show" : "hideAll.hide");

        try {
            if (isolationVisibleIds) {
                if (!isShow) {
                    isolation.setIdsVisible(isolationVisibleIds.sortedIds, false);
                    perf.end({ totalAffected: isolationVisibleIds.size, isShow });
                    return;
                }

                setIsLoading(true);
                let affectedIds: number[];
                try {
                    affectedIds =
                        filteredObjectIds != null
                            ? bulkSelectAllFromFilteredTree({
                                  filteredObjectIds,
                                  dispatchHighlighted: () => {},
                                  parentMeasure: perf,
                              })
                            : await bulkSelectAllHierarchies({
                                  nodes: objects,
                                  dataAccess,
                                  abortSignal: abortController.current.signal,
                                  dispatchHighlighted: () => {},
                                  parentMeasure: perf,
                              });
                } finally {
                    setIsLoading(false);
                }
                isolation.setIdsVisible(affectedIds, true);
                perf.end({ totalAffected: affectedIds.length, isShow });
                return;
            }

            let affectedIds: number[];
            setIsLoading(true);
            try {
                if (filteredObjectIds != null) {
                    // Filtered tree: parallel per-row fetches, single dispatch.
                    affectedIds = isShow
                        ? await bulkShowAllFromFilteredTree({
                              nodes: objects,
                              filteredObjectIds,
                              dispatchHidden,
                              dataAccess,
                              abortSignal: abortController.current.signal,
                              parentMeasure: perf,
                          })
                        : await bulkHideAllFromFilteredTree({
                              nodes: objects,
                              filteredObjectIds,
                              dispatchHidden,
                              dataAccess,
                              abortSignal: abortController.current.signal,
                              parentMeasure: perf,
                          });
                } else {
                    // Normal tree: parallel network fetches, single dispatch.
                    affectedIds = isShow
                        ? await bulkShowAllHierarchies({
                              nodes: objects,
                              dataAccess,
                              abortSignal: abortController.current.signal,
                              dispatchHidden,
                              parentMeasure: perf,
                          })
                        : await bulkHideAllHierarchies({
                              nodes: objects,
                              dataAccess,
                              abortSignal: abortController.current.signal,
                              dispatchHidden,
                              parentMeasure: perf,
                          });
                }
            } finally {
                setIsLoading(false);
            }

            perf.mark("dispatchComplete", { affectedIds: affectedIds.length });

            if (affectedIds.length > 0) {
                const shrunkAffected = snapshotUndoIds(affectedIds);
                dispatch(
                    undoRedoActions.push({
                        description: isShow ? "Show objects" : "Hide objects",
                        undoCallback: () => {
                            const ids = restoreUndoIds(shrunkAffected);
                            dispatchHidden(isShow ? hiddenActions.add(ids) : hiddenActions.remove(ids));
                        },
                        redoCallback: () => {
                            const ids = restoreUndoIds(shrunkAffected);
                            dispatchHidden(isShow ? hiddenActions.remove(ids) : hiddenActions.add(ids));
                        },
                    }),
                );
            }

            perf.end({ totalAffected: affectedIds.length, isShow });
        } catch (error) {
            perf.end({ error: true });
            if (!abortController.current.signal.aborted) {
                console.error("Global visibility toggle failed:", error);
            }
        }
    };

    const actions = createBulkActions(selectedCount, effectiveMainObjects, {
        t,
        dispatch,
        store,
        isolationVisibility: isolation,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        defaultVisibility,
        preIsolationVisibility,
        flyToSelected,
        currentMainObject: mainObject,
        hiddenIds: hiddenIdsRecord,
        lastHiddenIds: lastHiddenIds,
        geometryIds,
    });

    const content = (
        <>
            {isLoading ? (
                <CircularProgress size={20} />
            ) : (
                <Checkbox
                    checked={rootSelectionState === "all"}
                    indeterminate={rootSelectionState === "some"}
                    onChange={handleGlobalCheckboxChange}
                    disabled={isCheckboxDisabled}
                    aria-label={t("selectAll")}
                    data-testid="global-checkbox"
                />
            )}

            <ChevronButton
                disabled={isFullyCollapsed}
                collapsed={isFullyCollapsed}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isFullyCollapsed) {
                        onCollapseAll();
                    }
                }}
                role="button"
                aria-label={t("collapseAll")}
            >
                <ChevronRight style={{ color: colors.gray60 }} />
            </ChevronButton>

            {hasSelection ? (
                <>
                    <Dropdown
                        label={t("actions")}
                        variant="tertiary"
                        size="sm"
                        data-testid="bulk-actions-dropdown-button"
                        onSelect={({ item }) => {
                            const action = actions.find((a) => a.id === item);
                            if (action && !action.disabled) {
                                action.onClick();
                            }
                        }}
                    >
                        {actions.map((action) => (
                            <Dropdown.Item
                                key={action.id}
                                item={action.id}
                                disabled={action.disabled}
                                data-testid={`bulk-action-${action.id}`}
                            >
                                {action.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown>

                    <Box style={{ flex: 1 }} />

                    <Typography style={{ fontSize: "13px", color: colors.gray15 }}>
                        {filteredObjectIds !== null
                            ? t("propertiesOfTotal", { selectedCount, totalCount: filteredObjectIds.size })
                            : t("selectedCount", { count: selectedCount })}
                    </Typography>
                </>
            ) : (
                <>
                    <Box style={{ flex: 1 }} />
                    {filteredObjectIds !== null && (
                        <Typography style={{ fontSize: "13px", color: colors.gray15 }}>
                            {t("objectCount", { count: filteredObjectIds.size })}
                        </Typography>
                    )}
                </>
            )}

            <VisibilityButton
                disabled={isVisibilityDisabled}
                onClick={(e) => {
                    if (isVisibilityDisabled) {
                        return;
                    }
                    e.stopPropagation();
                    handleGlobalVisibilityToggle();
                }}
                role="button"
            >
                {effectiveVisibilityState === "all" ? (
                    <EyeOff style={{ width: 16, height: 16 }} />
                ) : (
                    <Eye style={{ width: 16, height: 16 }} />
                )}
            </VisibilityButton>
        </>
    );

    if (unwrapped) {
        return content;
    }

    return (
        <Box
            style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                paddingLeft: "16px",
                paddingRight: "16px",
                paddingTop: "8px",
                paddingBottom: "8px",
                borderBottom,
                backgroundColor: hasSelection ? colors.blue96 : colors.white,
                transition: "background-color 0.2s ease",
            }}
        >
            {content}
        </Box>
    );
}
